import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Alert, Spinner, Badge, Modal } from 'flowbite-react';
import {
  HiUser, HiOfficeBuilding, HiPencil, HiPlus,
  HiArrowLeft, HiIdentification, HiLocationMarker
} from 'react-icons/hi';
import { personsApi } from '../api/persons';
import { staffApi, type StaffAssignment } from '../api/staff';
import { getUserErrorMessage } from '../api';
import { descriptorsApi } from '../api/descriptors';
import { usePersonPermissions } from '../hooks/usePersonPermissions';
import { ContactBadge } from '../components/person/ContactBadge';
import { AddressBadge } from '../components/person/AddressBadge';
import { OrganizationAssignmentCard } from '../components/person/OrganizationAssignmentCard';
import { AssignmentsManager } from '../components/staff/AssignmentsManager';
import { EditPersonInfoModal } from '../components/modals/EditPersonInfoModal';
import { EditPersonEmailModal } from '../components/modals/EditPersonEmailModal';
import { EditPersonPhoneModal } from '../components/modals/EditPersonPhoneModal';
import { EditPersonAddressModal } from '../components/modals/EditPersonAddressModal';
import { AddRoleTagModal } from '../components/modals/AddRoleTagModal';
import type {
  PersonResponse,
  PersonEmailAssociation,
  PersonPhoneAssociation,
  PersonAddressResponse,
  PersonAddressCreate,
  PersonAddressUpdate
} from '../types/api';

// Helper to format date strings without timezone offset issues
const formatDateLocal = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString();
};

export const PersonProfilePage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [person, setPerson] = useState<PersonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Modal states
  const [editPersonModalOpen, setEditPersonModalOpen] = useState(false);
  const [editEmailModalOpen, setEditEmailModalOpen] = useState(false);
  const [editPhoneModalOpen, setEditPhoneModalOpen] = useState(false);
  const [editAddressModalOpen, setEditAddressModalOpen] = useState(false);
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);

  // Current items being edited
  const [currentEmail, setCurrentEmail] = useState<PersonEmailAssociation | null>(null);
  const [currentPhone, setCurrentPhone] = useState<PersonPhoneAssociation | null>(null);
  const [currentAddress, setCurrentAddress] = useState<PersonAddressResponse | null>(null);
  const [currentOrgId, setCurrentOrgId] = useState<number | null>(null);

  // Assignments manager modal state
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [staffAssignments, setStaffAssignments] = useState<StaffAssignment[]>([]);

  // Staff role descriptors for role tag dropdown
  const [staffRoleDescriptors, setStaffRoleDescriptors] = useState<Array<{ value: number; label: string }>>([]);

  // Include past assignments preference (persisted to localStorage)
  const [includePastAssignments, setIncludePastAssignments] = useState(() => {
    return localStorage.getItem('aiu-include-past-assignments') === 'true';
  });

  // Use permissions hook
  const permissions = usePersonPermissions(person);

  // Helper to check if a role is current (no endDate or endDate in the future)
  const isRoleCurrent = (role: { endDate: string | null }) => {
    if (!role.endDate) return true;
    return new Date(role.endDate) >= new Date();
  };

  // Handle toggling past assignments with localStorage persistence
  const handleTogglePastAssignments = (checked: boolean) => {
    setIncludePastAssignments(checked);
    localStorage.setItem('aiu-include-past-assignments', String(checked));
  };

  // Group organizations by type for display, filtering by active status
  const groupedOrganizations = useMemo(() => {
    if (!person) return {};

    // Filter roles based on whether we're including past assignments
    const filteredRoles = includePastAssignments
      ? person.roles
      : person.roles.filter(isRoleCurrent);

    const grouped: Record<number, {
      organization: any;
      primaryAssignment: any;
      roleTags: any[];
    }> = {};

    filteredRoles.forEach(role => {
      if (!grouped[role.educationOrganizationId]) {
        grouped[role.educationOrganizationId] = {
          organization: {
            educationOrganizationId: role.educationOrganizationId,
            organizationName: role.organizationName,
            organizationType: role.organizationType,
            leaId: role.leaId,
            parentLeaEducationOrganizationId: role.parentLeaEducationOrganizationId,
            schoolId: role.schoolId
          },
          primaryAssignment: null,
          roleTags: []
        };
      }

      if (role.includeInDirectory) {
        grouped[role.educationOrganizationId].primaryAssignment = role;
      } else {
        grouped[role.educationOrganizationId].roleTags.push(role);
      }
    });

    return grouped;
  }, [person, includePastAssignments]);

  // Deep link from the role tag gap alert: ?addRoleTag={educationOrganizationId}
  // opens Add Role Tag for that organization once the profile has loaded,
  // then drops the param so a refresh doesn't reopen it.
  useEffect(() => {
    const requestedOrgId = Number(searchParams.get('addRoleTag'));
    if (!requestedOrgId || loading || !person) return;

    const group = groupedOrganizations[requestedOrgId];
    if (group && permissions.canManageRolesForOrg(requestedOrgId, group.organization.parentLeaEducationOrganizationId)) {
      setCurrentOrgId(requestedOrgId);
      setAddRoleModalOpen(true);
    } else if (group) {
      setNotice(`You can view this contact, but adding role tags at ${group.organization.organizationName} needs an editor for that organization.`);
    }

    const remaining = new URLSearchParams(searchParams);
    remaining.delete('addRoleTag');
    setSearchParams(remaining, { replace: true });
  }, [searchParams, loading, person, groupedOrganizations]);

  // Load person data
  useEffect(() => {
    if (personId) {
      loadPerson();
    }
  }, [personId]);

  // Load staff role descriptors for role tag dropdown
  useEffect(() => {
    loadStaffRoleDescriptors();
  }, []);

  const loadStaffRoleDescriptors = async () => {
    try {
      const descriptors = await descriptorsApi.getByTypeName('Staff Role');
      const formatted = descriptors.map(d => ({
        value: d.descriptorId,
        label: d.shortDescription
      }));
      setStaffRoleDescriptors(formatted);
    } catch (err) {
      console.error('Failed to load staff role descriptors:', err);
      // Don't show error to user - role tags can work with custom roles too
    }
  };

  const loadPerson = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await personsApi.get(Number(personId));
      setPerson(data);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to load person'));
    } finally {
      setLoading(false);
    }
  };

  // Person info handlers
  const handleSavePersonInfo = async (updates: Partial<PersonResponse>) => {
    if (!person) return;

    try {
      const updated = await personsApi.update(person.personId, updates);
      setPerson(updated);
      setSuccessMessage('Person information updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  };

  // Email handlers
  const handleAddEmail = () => {
    setCurrentEmail(null);
    setEditEmailModalOpen(true);
  };

  const handleEditEmail = (email: PersonEmailAssociation) => {
    setCurrentEmail(email);
    setEditEmailModalOpen(true);
  };

  const handleSaveEmail = async (emailData: Partial<PersonEmailAssociation>) => {
    if (!person) return;

    try {
      if (currentEmail) {
        // Update existing
        await personsApi.updateEmail(
          person.personId,
          currentEmail.personEmailAssociationId,
          emailData
        );
      } else {
        // Add new
        await personsApi.addEmail(person.personId, emailData);
      }

      // Reload person data
      await loadPerson();
      setSuccessMessage(currentEmail ? 'Email updated successfully' : 'Email added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteEmail = async (emailId: number) => {
    if (!person || !confirm('Are you sure you want to delete this email address?')) return;

    try {
      await personsApi.deleteEmail(person.personId, emailId);
      await loadPerson();
      setSuccessMessage('Email deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete email');
    }
  };

  // Phone handlers
  const handleAddPhone = () => {
    setCurrentPhone(null);
    setEditPhoneModalOpen(true);
  };

  const handleEditPhone = (phone: PersonPhoneAssociation) => {
    setCurrentPhone(phone);
    setEditPhoneModalOpen(true);
  };

  const handleSavePhone = async (phoneData: Partial<PersonPhoneAssociation>) => {
    if (!person) return;

    try {
      if (currentPhone) {
        // Update existing
        await personsApi.updatePhone(
          person.personId,
          currentPhone.personPhoneAssociationId,
          phoneData
        );
      } else {
        // Add new
        await personsApi.addPhone(person.personId, phoneData);
      }

      // Reload person data
      await loadPerson();
      setSuccessMessage(currentPhone ? 'Phone updated successfully' : 'Phone added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  };

  const handleDeletePhone = async (phoneId: number) => {
    if (!person || !confirm('Are you sure you want to delete this phone number?')) return;

    try {
      await personsApi.deletePhone(person.personId, phoneId);
      await loadPerson();
      setSuccessMessage('Phone deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete phone');
    }
  };

  // Address handlers (SuperAdmin only)
  const handleAddAddress = () => {
    setCurrentAddress(null);
    setEditAddressModalOpen(true);
  };

  const handleEditAddress = (address: PersonAddressResponse) => {
    setCurrentAddress(address);
    setEditAddressModalOpen(true);
  };

  const handleSaveAddress = async (addressData: PersonAddressCreate | PersonAddressUpdate) => {
    if (!person) return;

    try {
      if (currentAddress) {
        // Update existing
        await personsApi.updateAddress(
          person.personId,
          currentAddress.personAddressId,
          addressData as PersonAddressUpdate
        );
      } else {
        // Add new
        await personsApi.addAddress(person.personId, addressData as PersonAddressCreate);
      }

      // Reload person data
      await loadPerson();
      setSuccessMessage(currentAddress ? 'Address updated successfully' : 'Address added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!person || !confirm('Are you sure you want to delete this address?')) return;

    try {
      await personsApi.deleteAddress(person.personId, addressId);
      await loadPerson();
      setSuccessMessage('Address deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete address');
    }
  };

  // Role tag handlers
  const handleAddRoleTag = (organizationId: number) => {
    setCurrentOrgId(organizationId);
    setAddRoleModalOpen(true);
  };

  const handleSaveRoleTag = async (roleData: any) => {
    if (!person || !currentOrgId) return;

    try {
      await personsApi.addRoleTag(person.personId, currentOrgId, {
        ...roleData,
        includeInDirectory: false  // Role tags are always false
      });

      await loadPerson();
      setSuccessMessage('Role tag added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteRoleTag = async (assignmentId: number) => {
    if (!person || !confirm('Are you sure you want to remove this role tag?')) return;

    try {
      // Find the role to get org ID
      const role = person.roles.find(r => r.assignmentId === assignmentId);
      if (!role) return;

      await personsApi.deleteRoleTag(
        person.personId,
        role.educationOrganizationId,
        assignmentId
      );

      await loadPerson();
      setSuccessMessage('Role tag removed successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove role tag');
    }
  };

  // Assignment management handlers
  const handleOpenAssignmentsManager = async () => {
    if (!person?.staffId) return;

    try {
      const fullStaff = await staffApi.get(person.staffId);
      setStaffAssignments(fullStaff.assignments);
      setShowAssignmentsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    }
  };

  const handleAssignmentsUpdate = async () => {
    // Refresh both the staff assignments in the modal and the person data
    if (person?.staffId) {
      try {
        const updatedStaff = await staffApi.get(person.staffId);
        setStaffAssignments(updatedStaff.assignments);
      } catch (err) {
        console.error('Failed to refresh staff assignments:', err);
      }
    }
    await loadPerson();
  };

  if (loading) {
    return (
      <div className="container mx-auto p-8">
        <div className="flex justify-center items-center h-64">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  if (error && !person) {
    return (
      <div className="container mx-auto p-8">
        <Alert color="failure">
          <div className="flex items-center gap-2">
            <span>{error}</span>
            <button
              onClick={loadPerson}
              className="ml-4 text-sm underline hover:no-underline"
            >
              Try Again
            </button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="container mx-auto p-8">
        <Alert color="warning">
          Person not found
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Go back"
          >
            <HiArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {person.displayName}
          </h1>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {notice && (
        <Alert color="info" onDismiss={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {/* Overview Card with Personal and Contact Information */}
      <Card className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <HiUser className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{person.displayName}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Personal Profile</p>
            </div>
          </div>
          {permissions.canEditBasicInfo && (
            <button
              onClick={() => setEditPersonModalOpen(true)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
              title="Edit personal information"
              aria-label="Edit personal information"
            >
              <HiPencil className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Basic Information */}
        {(person.preferredName || person.birthDate) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {person.preferredName && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Preferred Name</p>
                <p className="font-medium text-gray-900 dark:text-white">{person.preferredName}</p>
              </div>
            )}

            {person.birthDate && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Birth Date</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDateLocal(person.birthDate)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Contact Information Section */}
        <div className="border-t dark:border-gray-700 pt-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>

          {/* Email Addresses */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Addresses</h4>
              {permissions.canEditBasicInfo && (
                <button
                  onClick={handleAddEmail}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                >
                  <HiPlus className="inline w-3 h-3 mr-1" />
                  Add Email
                </button>
              )}
            </div>
            {person.emails.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {person.emails.map(email => (
                  <ContactBadge
                    key={email.personEmailAssociationId}
                    type="email"
                    value={email.emailAddress}
                    isPrimary={email.primaryEmailIndicator}
                    typeName={email.emailTypeName}
                    canEdit={permissions.canEditBasicInfo}
                    onEdit={() => handleEditEmail(email)}
                    onDelete={() => handleDeleteEmail(email.personEmailAssociationId)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">No email addresses on file</p>
            )}
          </div>

          {/* Phone Numbers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone Numbers</h4>
              {permissions.canEditBasicInfo && (
                <button
                  onClick={handleAddPhone}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                >
                  <HiPlus className="inline w-3 h-3 mr-1" />
                  Add Phone
                </button>
              )}
            </div>
            {person.phones.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {person.phones.map(phone => (
                  <ContactBadge
                    key={phone.personPhoneAssociationId}
                    type="phone"
                    value={phone.phoneNumber}
                    extension={phone.extension}
                    isPrimary={phone.primaryPhoneIndicator}
                    typeName={phone.phoneTypeName}
                    canEdit={permissions.canEditBasicInfo}
                    onEdit={() => handleEditPhone(phone)}
                    onDelete={() => handleDeletePhone(phone.personPhoneAssociationId)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">No phone numbers on file</p>
            )}
          </div>

          {/* Addresses Section - SuperAdmin or OrgAdmin with access */}
          {permissions.canViewAddresses && (
            <div className="mt-6 pt-6 border-t dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <HiLocationMarker className="w-4 h-4 text-purple-500" />
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Home Addresses</h4>
                  <Badge size="xs" color="purple">Admin Only</Badge>
                </div>
                <button
                  onClick={handleAddAddress}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                >
                  <HiPlus className="inline w-3 h-3 mr-1" />
                  Add Address
                </button>
              </div>
              {person.addresses && person.addresses.length > 0 ? (
                <div className="space-y-2">
                  {person.addresses.map(address => (
                    <AddressBadge
                      key={address.personAddressId}
                      address={address}
                      canEdit={true}
                      onEdit={() => handleEditAddress(address)}
                      onDelete={() => handleDeleteAddress(address.personAddressId)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No addresses on file</p>
              )}
            </div>
          )}
        </div>

        {/* Identification Codes */}
        {person.identificationCodes.length > 0 && (
          <div className="mt-6 pt-6 border-t dark:border-gray-700">
            <div className="flex items-center gap-2 mb-2">
              <HiIdentification className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Identification Codes</p>
            </div>
            <div className="space-y-1">
              {person.identificationCodes.map(code => (
                <div key={code.personIdentificationCodeId} className="flex items-center gap-2">
                  <span className="text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-0.5 rounded">
                    {code.identificationCode}
                  </span>
                  {code.systemDescriptorName && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({code.systemDescriptorName})
                    </span>
                  )}
                  {code.isPrimary && (
                    <Badge size="xs" color="info">Primary</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Organization Assignments */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <HiOfficeBuilding className="w-7 h-7 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Organization Assignments</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Positions and roles at education organizations</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={includePastAssignments}
                onChange={(e) => handleTogglePastAssignments(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
              />
              Include past assignments
            </label>
            {permissions.canEditBasicInfo && person?.staffId && (
              <button
                onClick={handleOpenAssignmentsManager}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/50 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
              >
                <HiPencil className="w-4 h-4" />
                Manage Assignments
              </button>
            )}
          </div>
        </div>

        {Object.keys(groupedOrganizations).length > 0 ? (
          <div className="space-y-4">
            {Object.values(groupedOrganizations).map(({ organization, primaryAssignment, roleTags }) => (
              <OrganizationAssignmentCard
                key={organization.educationOrganizationId}
                organization={organization}
                primaryAssignment={primaryAssignment}
                roleTags={roleTags}
                inheritedRoleTags={(() => {
                  const district = organization.organizationType === 'school' && organization.parentLeaEducationOrganizationId
                    ? groupedOrganizations[organization.parentLeaEducationOrganizationId]
                    : undefined;
                  return district && !district.primaryAssignment ? district.roleTags : [];
                })()}
                canEdit={permissions.canManageRolesForOrg(
                  organization.educationOrganizationId,
                  organization.parentLeaEducationOrganizationId  // Parent LEA's educationOrganizationId for permission checking
                )}
                onAddRole={() => handleAddRoleTag(organization.educationOrganizationId)}
                onDeleteRole={handleDeleteRoleTag}
              />
            ))}
          </div>
        ) : (
          <Card className="p-6">
            <p className="text-gray-500 dark:text-gray-400 text-center italic">
              No organization assignments on record
            </p>
          </Card>
        )}
      </div>

      {/* Modals */}
      {editPersonModalOpen && (
        <EditPersonInfoModal
          isOpen={editPersonModalOpen}
          onClose={() => setEditPersonModalOpen(false)}
          person={person}
          onSave={handleSavePersonInfo}
        />
      )}

      {editEmailModalOpen && (
        <EditPersonEmailModal
          isOpen={editEmailModalOpen}
          onClose={() => {
            setEditEmailModalOpen(false);
            setCurrentEmail(null);
          }}
          email={currentEmail}
          onSave={handleSaveEmail}
        />
      )}

      {editPhoneModalOpen && (
        <EditPersonPhoneModal
          isOpen={editPhoneModalOpen}
          onClose={() => {
            setEditPhoneModalOpen(false);
            setCurrentPhone(null);
          }}
          phone={currentPhone}
          onSave={handleSavePhone}
        />
      )}

      {editAddressModalOpen && (
        <EditPersonAddressModal
          isOpen={editAddressModalOpen}
          onClose={() => {
            setEditAddressModalOpen(false);
            setCurrentAddress(null);
          }}
          address={currentAddress}
          onSave={handleSaveAddress}
        />
      )}

      {addRoleModalOpen && currentOrgId && (
        <AddRoleTagModal
          isOpen={addRoleModalOpen}
          onClose={() => {
            setAddRoleModalOpen(false);
            setCurrentOrgId(null);
          }}
          organizationId={currentOrgId}
          organizationName={
            groupedOrganizations[currentOrgId]?.organization.organizationName || ''
          }
          positionTitle={groupedOrganizations[currentOrgId]?.primaryAssignment?.positionTitle}
          existingRoleIds={
            [
              groupedOrganizations[currentOrgId]?.primaryAssignment,
              ...(groupedOrganizations[currentOrgId]?.roleTags || [])
            ]
              .map((role: { roleDescriptorId: number | null } | null | undefined) => role?.roleDescriptorId)
              .filter((id: number | null | undefined): id is number => id != null)
          }
          availableRoles={staffRoleDescriptors}
          onSave={handleSaveRoleTag}
        />
      )}

      {/* Manage Assignments Modal */}
      {person?.staffId && (
        <Modal
          show={showAssignmentsModal}
          onClose={() => setShowAssignmentsModal(false)}
          dismissible
          size="3xl"
        >
          <div className="flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4 border-b dark:border-gray-700 flex-shrink-0">
              <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                Manage Assignments: {person.displayName}
              </h3>
              <button
                onClick={() => setShowAssignmentsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AssignmentsManager
                staffId={person.staffId}
                assignments={staffAssignments}
                onUpdate={handleAssignmentsUpdate}
              />
            </div>

            {/* Footer */}
            <div className="p-6 pt-4 border-t dark:border-gray-700 flex justify-end flex-shrink-0">
              <button
                onClick={() => setShowAssignmentsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};